import { z } from "zod";
import { API_PREFIX, paginatedFromDto, type HttpClient } from "@nasam/kernel";
import type { SubscriptionsRepository, SubscriptionSale } from "../domain";
import {
  subscriptionDetailsFromDto,
  subscriptionFromDto,
  subscriptionToDto,
  moduleInputToDto,
} from "./subscriptionMappers";
import {
  subscriptionAddModulesWriteDtoSchema,
  subscriptionDeviceAllocationWriteDtoSchema,
  subscriptionDetailsResponseDtoSchema,
  subscriptionDeviceModelsResponseDtoSchema,
  subscriptionDeviceTypesResponseDtoSchema,
  subscriptionListResponseDtoSchema,
  subscriptionResponseDtoSchema,
  subscriptionSaleResponseDtoSchema,
  subscriptionWriteDtoSchema,
} from "./subscriptionDtos";

const SUBSCRIPTIONS_PATH = `${API_PREFIX}/subscriptions`;

/** The subscription a sale answers with, and where its modules now stand. */
function saleFromResponse(response: unknown): SubscriptionSale {
  const { data } = subscriptionSaleResponseDtoSchema.parse(response);
  return { subscription: subscriptionFromDto(data), usage: data.usageInfo };
}

export function createSubscriptionsHttpRepository(
  http: HttpClient,
): SubscriptionsRepository {
  return {
    async list(query) {
      const response = await http.get(SUBSCRIPTIONS_PATH, { params: query });
      return paginatedFromDto(
        subscriptionListResponseDtoSchema.parse(response),
        subscriptionFromDto,
      );
    },
    async getById(id) {
      const response = await http.get(`${SUBSCRIPTIONS_PATH}/${id}`);
      return subscriptionDetailsFromDto(
        subscriptionDetailsResponseDtoSchema.parse(response).data,
      );
    },
    async create(input) {
      const body = subscriptionWriteDtoSchema.parse(subscriptionToDto(input));
      return saleFromResponse(await http.post(SUBSCRIPTIONS_PATH, body));
    },
    async addModules(id, modules) {
      const body = subscriptionAddModulesWriteDtoSchema.parse({
        modules: modules.map(moduleInputToDto),
      });
      return saleFromResponse(
        await http.post(`${SUBSCRIPTIONS_PATH}/${id}/modules`, body),
      );
    },

    // Pending-contract (see docs/api/gaps.md) below this line.
    async addDevices(id, devices) {
      const body = {
        devices: z
          .array(subscriptionDeviceAllocationWriteDtoSchema)
          .parse(devices),
      };
      const response = await http.post(
        `${SUBSCRIPTIONS_PATH}/${id}/devices`,
        body,
      );
      return subscriptionFromDto(
        subscriptionResponseDtoSchema.parse(response).data,
      );
    },
    async listDeviceTypes() {
      const response = await http.get(`${SUBSCRIPTIONS_PATH}/device-types`);
      return subscriptionDeviceTypesResponseDtoSchema
        .parse(response)
        .data.map((type) => ({ ...type }));
    },
    async listDeviceModels(deviceTypeId) {
      const response = await http.get(
        `${SUBSCRIPTIONS_PATH}/device-types/${deviceTypeId}/models`,
      );
      return subscriptionDeviceModelsResponseDtoSchema
        .parse(response)
        .data.map((model) => ({ ...model }));
    },
  };
}
